import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'framer-motion';
import React, { useRef } from 'react';

import useCurrentLang from '@/hooks/apps/useCurrentLang';
import { useDeferredVideo } from '@/hooks/shared/useDeferredVideo';

import { ABOUT_HERO_BY_LOCALE } from '@/data/shared/aboutCopy';
import {
  ABOUT_GALLERY_BOTTOM,
  ABOUT_GALLERY_TOP,
} from '@/data/shared/aboutGallery';

import AniSlideUpBlur from '@/components/shared/animations/basic/AniSlideUpBlur';
import mapMotionProgress from '@/components/shared/animations/motion/mapMotionProgress';
import resolveMotionEase from '@/components/shared/animations/motion/resolveMotionEase';
import useSmoothedMotionValue from '@/components/shared/animations/motion/useSmoothedMotionValue';
import BlurImage from '@/components/shared/ui/BlurImage';

import {
  aboutScrollInner,
  aboutScrollVideo,
  aboutScrollWrap,
  aboutTitle,
  aboutVisualContainer,
  aboutVisualInner,
  aboutVisualWrap,
  imageInner,
  imageInnerToLeft,
  imageInnerToRight,
  imageListInner,
  imageListWrap,
  imageWrap,
} from './AboutVisual.styles';
import useGalleryTravel from './useGalleryTravel';

// 영상 국/영문 데이터
const VIDEO_SRC_DATA = {
  ko: '/images/apps/about/sec_02_video_01_3s_v2.m4v',
  en: '/images/apps/about/sec_02_video_01_en.m4v',
};

const ABOUT_IMAGE_SIZES =
  '(min-width: 1920px) 480px, (min-width: 960px) 25vw, 240px';

function primeScrubbableVideo(video: HTMLVideoElement) {
  void video
    .play()
    .then(() => {
      video.autoplay = false;
      video.currentTime = 0.000001;
      video.pause();
    })
    .catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      console.error('About video initialization failed', error);
    });
}

export default function AboutVisual() {
  const currentLang = useCurrentLang();
  const shouldReduceMotion = useReducedMotion();

  const videoContainer = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageContainer = useRef<HTMLDivElement>(null);
  const { rowRef: topRowRef, travel: topTravel } = useGalleryTravel(
    imageContainer,
    'left',
  );
  const { rowRef: bottomRowRef, travel: bottomTravel } = useGalleryTravel(
    imageContainer,
    'right',
  );

  const { scrollYProgress: videoProgress } = useScroll({
    target: videoContainer,
    offset: ['start start', 'end end'],
  });
  const { scrollYProgress: imageProgress } = useScroll({
    target: imageContainer,
    offset: ['start end', 'end start'],
  });
  const smoothedImageProgress = useSmoothedMotionValue(imageProgress, 1.02);
  const scrollEase = resolveMotionEase('power1.out');
  const topImageX = useTransform(smoothedImageProgress, (latest) => {
    const progress = mapMotionProgress(latest, 0, 1, scrollEase);
    return progress * -topTravel;
  });
  const bottomImageX = useTransform(smoothedImageProgress, (latest) => {
    const progress = mapMotionProgress(latest, 0, 1, scrollEase);
    return progress * bottomTravel;
  });

  useDeferredVideo({
    videoRef,
    src: VIDEO_SRC_DATA[currentLang],
    onActivate: primeScrubbableVideo,
  });

  useMotionValueEvent(videoProgress, 'change', (progress) => {
    const video = videoRef.current;
    if (!video || shouldReduceMotion) return;

    const scrubDuration = Math.min(video.duration || 3, 3);
    video.currentTime = scrollEase(progress) * scrubDuration;
  });

  return (
    <section className={aboutVisualContainer}>
      {/* 비주얼 (We make ~) */}
      <article className={aboutVisualWrap}>
        <div className={aboutVisualInner}>
          <AniSlideUpBlur blur={60}>
            <h2 className={aboutTitle}>
              We make <br />
              sound into insight
            </h2>
            <p
              className={
                'mt-6 text-[16px] font-medium leading-[140%] tracking-[-0.32px] text-apps-brandGrayWhite lg:text-[22px] lg:leading-[160%] lg:tracking-[-0.44px]'
              }
            >
              {ABOUT_HERO_BY_LOCALE[currentLang].lead}
            </p>
            <p
              className={
                'mt-2 text-[16px] font-medium leading-[140%] tracking-[-0.32px] text-apps-brandGrayWhite lg:text-[22px] lg:leading-[160%] lg:tracking-[-0.44px]'
              }
            >
              {ABOUT_HERO_BY_LOCALE[currentLang].description}
            </p>
          </AniSlideUpBlur>
        </div>
      </article>

      {/* 스크롤 영상 인터랙션 */}
      <article className={aboutScrollWrap} ref={videoContainer}>
        <div className={aboutScrollInner}>
          <video
            className={aboutScrollVideo}
            muted
            playsInline
            preload="none"
            poster="/images/apps/about/sec_02_video_01_poster.webp"
            aria-hidden="true"
            ref={videoRef}
          ></video>
        </div>
      </article>

      {/* 이미지 리스트 인터랙션 */}
      <div className={imageListWrap} ref={imageContainer}>
        <div className={imageListInner}>
          <motion.ul
            ref={topRowRef}
            className={`${imageInner} ${imageInnerToLeft}`}
            style={{ x: shouldReduceMotion ? 0 : topImageX }}
          >
            {ABOUT_GALLERY_TOP.map((image) => (
              <li key={image.src}>
                <div className={imageWrap}>
                  <BlurImage
                    src={image.src}
                    alt={image.alt[currentLang]}
                    style={{
                      objectFit: 'cover',
                      objectPosition: image.objectPosition,
                    }}
                    fill
                    sizes={ABOUT_IMAGE_SIZES}
                  />
                </div>
              </li>
            ))}
          </motion.ul>
          <motion.ul
            ref={bottomRowRef}
            className={`${imageInner} ${imageInnerToRight}`}
            style={{ x: shouldReduceMotion ? 0 : bottomImageX }}
          >
            {ABOUT_GALLERY_BOTTOM.map((image) => (
              <li key={image.src}>
                <div className={imageWrap}>
                  <BlurImage
                    src={image.src}
                    alt={image.alt[currentLang]}
                    style={{
                      objectFit: 'cover',
                      objectPosition: image.objectPosition,
                    }}
                    fill
                    sizes={ABOUT_IMAGE_SIZES}
                  />
                </div>
              </li>
            ))}
          </motion.ul>
        </div>
      </div>
    </section>
  );
}
